/**
 * ============================================================================
 * Specifikit Advanced Features JS
 * Location: assets/specifikit-advanced.js
 * Version: 1.0
 * Author: specifikit.com
 * ============================================================================
 */

/**
 * ============================================================================
 * Admin Nudge Manager
 * This self-contained class manages the admin-only notification for missing
 * cart snippets. It is resilient, dismissible, and provides a superior UX.
 * ============================================================================
 */
class AdminNudgeManager {
  constructor(i18n, logger) {
    this.element = null;
    this.isShown = false;
    this.storageKey = 'specifikit_admin_nudge_dismissed';
    this.i18n = i18n || {
      nudge_title: 'Specifikit Admin Assist: Action Required',
      nudge_message: 'Your cart is missing the customization display snippet. Please review the installation guide to add the \'{snippet_name}\' snippet to your cart template.',
      nudge_dismiss: 'Dismiss for session'
    };
    this.logger = logger; // Use the passed-in logger instance

    // Create the DOM element once.
    this._createElement();
  }

  _createElement() {
    if (document.getElementById('specifikit-admin-nudge')) return;

    this.element = document.createElement('div');
    this.element.id = 'specifikit-admin-nudge';
    this.element.setAttribute('role', 'alert');

    const messageContainer = document.createElement('div');
    messageContainer.className = 'specifikit-admin-nudge__message';
    messageContainer.innerHTML = `<strong>${this.i18n.nudge_title}</strong> <span data-nudge-text></span>`;
    
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'specifikit-admin-nudge__close-btn';
    closeButton.setAttribute('aria-label', this.i18n.nudge_dismiss);
    closeButton.innerHTML = '&times;';
    closeButton.addEventListener('click', () => this._handleClose());

    this.element.appendChild(messageContainer);
    this.element.appendChild(closeButton);
    
    document.body.appendChild(this.element);
  }

  show(replacements = {}, type = 'error') {
    if (!this.element || sessionStorage.getItem(this.storageKey) === 'true') {
      if (this.logger) this.logger.log('AdminNudge: Dismissed for this session, not showing.');
      return;
    }
    
    const messageTextElement = this.element.querySelector('[data-nudge-text]');
    if (messageTextElement) {
      let message = this.i18n.nudge_message;
      for (const key in replacements) {
        message = message.replace(`{${key}}`, `<code>${replacements[key]}</code>`);
      }
      messageTextElement.innerHTML = message;
    }

    this.element.dataset.type = type;
    this.element.classList.add('is-visible');
    this.isShown = true;
    if (this.logger) this.logger.warn('AdminNudge: Displaying warning to administrator.');
  }

  hide() {
    if (!this.element || !this.isShown) {
      return;
    }
    this.element.classList.remove('is-visible');
    this.isShown = false;
  }

  _handleClose() {
    this.hide();
    try {
      sessionStorage.setItem(this.storageKey, 'true');
      if (this.logger) this.logger.log('AdminNudge: Dismissed for this session.');
    } catch (e) {
      if (this.logger) this.logger.warn('AdminNudge: Could not set sessionStorage. Nudge may reappear.');
    }
  }
}


class SpecifikitAdvancedFeatures {
  constructor(customizerInstance) {
    if (!customizerInstance || !customizerInstance.logger) {
      console.error('❌ Advanced: Core customizer instance with a logger is required for advanced features to run.');
      return;
    }

    this.customizer = customizerInstance;
    this.config = this.customizer.config;
    this.container = this.customizer.container;
    this.logger = this.customizer.logger; // Inherit the logger from the core instance
    this.elements = {};
    this.state = {
      view360Active: false,
      zoomActive: false,
      comparisonMode: false,
      savedConfigs: this.loadSavedConfigsFromStorage()
    };
    this.modelViewerLoaded = false;
    this.isAdmin = window.Shopify?.designMode || /shopify_user_id=\d+/.test(document.cookie);
    this.i18n = this.loadTranslations();
    
    if (this.isAdmin) {
      this.adminNudge = new AdminNudgeManager(this.i18n.admin, this.logger);
    }
    
    // Track initialized contexts
    this.initializedContexts = new Set();

    // Load feature flags
    const featureFlagsEl = document.querySelector('[data-advanced-features-config]');
    if (featureFlagsEl) {
      this.featureFlags = JSON.parse(featureFlagsEl.textContent);
    } else {
      this.logger.warn('Advanced Features: Config data island not found. Features may not initialize correctly.');
      this.featureFlags = {};
    }

    // --- START: UI CONFIG LOADING ---
    const featureUiConfigEl = document.querySelector('[data-specifikit-feature-ui-config]');
    this.featureUiConfig = { icon_toolbar_display: 'disabled', text_button_layout_desktop: 'default_split' }; // Set defaults
    if (featureUiConfigEl) {
        try {
            const uiConfig = JSON.parse(featureUiConfigEl.textContent);
            this.featureUiConfig = { ...this.featureUiConfig, ...uiConfig };
        } catch(e) {
            this.logger.error("Could not parse UI config data island.", e);
        }
    } else {
        this.logger.warn('Advanced Features: UI Config data island not found. Using defaults.');
    }
    // --- END: UI CONFIG LOADING ---

    // Initialize based on display mode
    if (this.config.displayMode === 'modal') {
      this.initModalMode();
    } else {
      this.initInlineMode();
    }
  }

  /**
   * [MODAL MODE] Complete initialization strategy
   */
  initModalMode() {
    this.logger.info('🎭 Advanced Features: Initializing for MODAL mode');
    
    // Phase 1: Initialize page context (before modal exists)
    // Exclude features that are modal-only from the main page context
    requestAnimationFrame(() => {
      this.initializeContext('page', this.container, { exclude: ['360View', 'ImageZoom', 'SaveShare', 'PriceBreakdown', 'ComparisonMode', 'Wishlist'] });
    });
    
    // Phase 2: Listen for modal ready event
    this.container.addEventListener('specifikit:modalReady', (e) => {
      this.logger.info('🎬 Advanced Features: Modal ready, initializing modal context');
      requestAnimationFrame(() => {
        this.injectModalPlaceholders(e.detail.modalElement);
        this.initializeContext('modal', e.detail.modalElement);
      });
    }, { once: false });
    
    // Phase 3: Failsafe - reinitialize on modal open
    this.container.addEventListener('specifikit:modalOpened', () => {
      setTimeout(() => {
        const modalElement = document.querySelector('.specifikit-modal-view.is-visible');
        if (modalElement && !this.initializedContexts.has('modal')) {
          this.logger.warn('Failsafe modal initialization triggered');
          this.injectModalPlaceholders(modalElement);
          this.initializeContext('modal', modalElement);
        }
      }, 100);
    });
  }

  /**
   * [INLINE MODE] Standard initialization
   */
  initInlineMode() {
    this.logger.info('📄 Advanced Features: Initializing for INLINE mode');
    requestAnimationFrame(() => {
      this.initializeContext('inline', this.container);
    });
  }

  /**
   * [UNIVERSAL] Context-aware initialization
   * @param {string} context - 'page', 'modal', or 'inline'
   * @param {HTMLElement} scope - The DOM scope to initialize within
   * @param {object} options - Optional configuration, e.g., { exclude: ['FeatureName'] }
   */
  initializeContext(context, scope, options = {}) {
    if (!scope) {
      this.logger.error(`Cannot initialize ${context} context - scope is missing`);
      return;
    }
    
    if (this.initializedContexts.has(context)) {
      this.logger.log(`Advanced Features: ${context} context already initialized, skipping`);
      return;
    }
    
    this.logger.info(`✨ Initializing ${context} context...`);
    
    const exclude = options.exclude || [];
    
    // Query elements in this scope
    this.queryElements(scope);
    
    // Attach core listeners (only once)
    if (!this.listenersAttached) {
      this.attachCoreListeners();
    }
    
    // NEW: Centralized UI rendering for advanced feature buttons
    if (!exclude.includes('SaveShare') && !exclude.includes('ComparisonMode')) {
        this._setupAdvancedUI(scope);
    }

    // Initialize features based on context
    const allFeatures = {
        'ImageZoom': () => this.setupImageZoom(scope),
        '360View': () => this.setup360View(scope),
        'ARPreview': () => this.setupARPreview(scope),
        'SaveShare': () => this.setupSaveShare(scope),
        'PriceBreakdown': () => this.setupPriceBreakdown(scope),
        'ComparisonMode': () => this.setupComparisonMode(scope),
        'Wishlist': () => this.setupWishlist(scope),
        'AdminCartChecker': () => this.setupAdminCartChecker(),
        'URLConfigLoader': () => this.setupURLConfigLoader()
    };
    
    for (const [featureName, setupFn] of Object.entries(allFeatures)) {
        if (!exclude.includes(featureName)) {
            // Check if feature is enabled via featureFlags (if applicable)
            const flagKey = `enable_${featureName.replace(/([A-Z])/g, '_$1').toLowerCase().substring(1)}`;
            const isEnabled = this.featureFlags[flagKey] !== undefined ? this.featureFlags[flagKey] : true; // Default to true if no flag exists (e.g., for internal features)
            
            this.setupFeature(featureName, setupFn, isEnabled);
        }
    }
    
    this.initializedContexts.add(context);
    this.logger.info(`✅ Advanced Features: ${context} context initialized successfully`);
  }

  /**
   * Injects necessary placeholders into the modal DOM before initialization.
   * This is critical for features like Price Breakdown that need a target to render into.
   * @param {HTMLElement} modalElement - The root element of the modal.
   */
  injectModalPlaceholders(modalElement) {
    if (!modalElement || modalElement.dataset.placeholdersInjected === 'true') {
      return;
    }

    const priceContainer = modalElement.querySelector('.specifikit-price-container');
    if (!priceContainer) {
      this.logger.warn('Advanced: Price container not found in modal to inject placeholders.');
      return;
    }

    // Inject Price Breakdown placeholder if the feature is enabled
    if (this.featureFlags.enable_price_breakdown) {
      if (!priceContainer.querySelector('[data-specifikit-placeholder="price-breakdown"]')) {
        const pbPlaceholder = document.createElement('div');
        pbPlaceholder.dataset.specifikitPlaceholder = 'price-breakdown';
        priceContainer.appendChild(pbPlaceholder);
        this.logger.log('✅ Injected Price Breakdown placeholder into modal.');
      }
    }
    
    modalElement.dataset.placeholdersInjected = 'true';
  }

  /**
   * [CONTEXT-AWARE] Query elements in the given scope
   */
  queryElements(scope) {
    if (!scope) {
      this.logger.error("Advanced Features: Cannot query elements - scope is missing");
      return;
    }
    
    this.elements = {
      mainImage: scope.querySelector('[data-specifikit-image-simple]'),
      imageLayerStack: scope.querySelector('[data-specifikit-image-layer-stack]'),
      imageContainer: scope.querySelector('[data-specifikit-image-container]'),
      summarySection: scope.querySelector('.specifikit-customizer-summary'),
      productInfo: scope.querySelector('.specifikit-product-info, .specifikit-modal-controls'),
      priceDisplayContainer: scope.querySelector('.specifikit-price-container'),
      priceBreakdownPlaceholder: scope.querySelector('[data-specifikit-placeholder="price-breakdown"]'),
      addToCartForm: scope.querySelector('[data-specifikit-form]'),
      wishlistButtonPlaceholder: scope.querySelector('[data-specifikit-placeholder="wishlist-button"]'),
      advancedFeaturesWrapper: scope.querySelector('[data-specifikit-advanced-features-wrapper]'),
      viewToolbar: scope.querySelector('[data-specifikit-view-toolbar]')
    };
  }

  loadTranslations() {
    const defaults = {
      cart: {
        preview_unavailable: "Preview Unavailable"
      },
      save_share: {
        save_button: "Save",
        share_button: "Share",
        load_button: "Load",
        save_prompt: "Name your configuration:",
        save_success: "Configuration saved!",
        load_modal_title: "Your Saved Configurations",
        load_this_button: "Load This",
        delete_button: "Delete",
        delete_confirm_title: "Confirm Deletion",
        delete_confirm_prompt: "This will permanently remove '<strong>{configName}</strong>'. This action cannot be undone.",
        delete_confirm_button: "Yes, Delete",
        delete_cancel_button: "Cancel",
        deleted_success: "Configuration deleted.",
        no_saved_configs: "You have no saved configurations yet.",
        no_saved_configs_prompt: "Click the '<strong>Save</strong>' button to store your favorite designs here for later.",
        share_modal_title: "Share Your Configuration",
        share_modal_prompt: "Copy the link below or use a share button.",
        share_email: "Email",
        share_whatsapp: "WhatsApp",
        share_done: "Done",
        link_copied: "Share link copied to clipboard!"
      },
      price_breakdown: {
        title: "Price Breakdown",
        base_price: "Base Price",
        total: "Total"
      },
      comparison: {
        button: "Compare",
        title: "Compare Configurations",
        current_config: "Current Configuration",
        currently_viewing: "Currently Viewing",
        load_this: "Load This",
        details_heading: "View Details",
        no_saved_configs: "Save some configurations to compare them here.",
        empty_slot_icon_alt: "Icon of an empty comparison slot.",
        overflow_notification: "Comparison limit reached. Oldest configuration removed."
      },
      wishlist: {
        button: "Add to Guest Wishlist",
        success: "Added to Guest Wishlist!",
        tooltip: "Saves your configuration to a temporary wishlist on this device only."
      },
      ar: {
        button: "View in Your Space", 
        loading: "Preparing AR viewer...",
        error_library: "Could not load AR library. Please check your connection.",
        error_modal: "Could not launch AR viewer. UI template is missing.",
        modal_title: "Augmented Reality Preview",
        model_viewer_status: "Loading 3D Model...",
        model_viewer_error: "Could not load 3D model.",
        model_viewer_error_detail: "Please check your connection or try again.",
        launch_button_text: "Launch AR",
        not_supported_title: "AR Not Supported",
        not_supported_message: "This device does not support augmented reality. You can still view the 3D model."
      },
      zoom: {
        button: "Zoom",
        lightbox_close: "Close",
        lightbox_zoom_in: "Zoom in",
        lightbox_zoom_out: "Zoom out",
        lightbox_download: "Download"
      },
      three_sixty: {
        button_360: "360° View",
        button_photo: "Photo View",
        notification: "Drag to rotate"
      }
    };

    try {
        const i18nEl = document.querySelector('[data-specifikit-i18n]');
        if (i18nEl) {
            const translations = JSON.parse(i18nEl.textContent);
            return Object.keys(defaults).reduce((acc, key) => {
                acc[key] = { ...defaults[key], ...(translations[key] || {}) };
                return acc;
            }, { ...translations });
        }
        return defaults;
    } catch (e) {
        this.logger.error("Could not parse i18n translations. Using defaults.", e);
        return defaults;
    }
  }

  attachCoreListeners() {
    if (this.listenersAttached) return;
    this.container.addEventListener('specifikit:stateUpdated', (e) => this.handleCoreStateUpdate(e));
    this.listenersAttached = true;
    this.logger.info('🎧 Advanced module is now listening for core updates.');
  }

  handleCoreStateUpdate(e) {
    if (this.featureFlags.enable_price_breakdown) {
      this.updatePriceBreakdown(e.detail);
    }
    
    // =================================================================
    // START: UX IMPROVEMENT FOR INITIAL VIEW
    // Show/hide the view toolbar when the initial state changes.
    // =================================================================
    const viewToolbar = this.container.querySelector('[data-specifikit-view-toolbar]');
    if (viewToolbar) {
        viewToolbar.style.display = e.detail.state.isInitialState ? 'none' : '';
    }
    // =================================================================
    // END: UX IMPROVEMENT
    // =================================================================
  }

  setupFeature(name, fn, isEnabled) {
    if (!isEnabled) return;
    try {
      fn.bind(this)();
    } catch (error) {
      this.logger.error(`Advanced Feature Init Failed [${name}]:`, error);
    }
  }

  // =================================================================
  // START: UNIVERSAL THEMED MODAL CREATION HELPER
  // =================================================================
  /**
   * Creates a themed modal/panel from a template, ensuring it inherits
   * all necessary classes and data attributes for consistent styling and
   * typography, regardless of where it's appended in the DOM.
   * @param {string} templateId The ID of the <template> element.
   * @param {string} rootSelector The selector for the root element within the template.
   * @returns {{modalElement: HTMLElement | null}} An object containing the themed modal element.
   */
  _createThemedModalFromTemplate(templateId, rootSelector) {
    const template = document.getElementById(templateId);
    if (!template) {
      this.logger.error(`Template with ID "${templateId}" not found.`);
      this.showNotification(this.i18n.ar.error_modal, 'error');
      return { modalElement: null };
    }

    const fragment = template.content.cloneNode(true);
    const modalElement = fragment.querySelector(rootSelector);

    if (!modalElement) {
      this.logger.error(`Root element with selector "${rootSelector}" not found in template "${templateId}".`);
      return { modalElement: null };
    }

    // Propagate the styling context.
    modalElement.classList.add('specifikit-product-customizer-wrapper');
    modalElement.dataset.sectionId = this.container.dataset.sectionId;
    modalElement.dataset.typographicIsland = this.container.dataset.typographicIsland;

    return { modalElement, fragment };
  }
  // ===============================================================
  // END: UNIVERSAL THEMED MODAL CREATION HELPER
  // ===============================================================

  // ==========================================
  // Centralized Advanced UI Engine
  // ==========================================
  _setupAdvancedUI(scope) {
    const wrapper = scope.querySelector('[data-specifikit-advanced-features-wrapper]');
    if (!wrapper || wrapper.dataset.uiRendered === 'true') {
        return;
    }
    wrapper.innerHTML = ''; // Clear placeholders

    const features = [];
    if (this.featureFlags.enable_save_share) {
        features.push({ id: 'save', text: this.i18n.save_share.save_button, action: this.showSaveDialog.bind(this), icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2zM17 21v-8H7v8h10zM7 3v5h8V3H7z"/></svg>` });
        features.push({ id: 'share', text: this.i18n.save_share.share_button, action: this.shareConfiguration.bind(this), icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>` });
        features.push({ id: 'load', text: this.i18n.save_share.load_button, action: this.showLoadModal.bind(this), icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M5 20h14v-2H5v2zm0-10h4v6h6v-6h4l-7-7-7 7z"/></svg>` });
    }
    if (this.featureFlags.enable_comparison_mode) {
        features.push({ id: 'compare', text: this.i18n.comparison.button, action: this.showComparisonView.bind(this), icon: `<svg xmlns="http://www.w3.org/2000/svg" class="specifikit-inline-svg" fill="currentColor" viewBox="0 0 24 24"><path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12C20,12.2 20,12.39 19.95,12.58L17.6,11.23C17.27,10.22 16.5,9.36 15.53,8.87L16.33,6.2C17.44,6.88 18.3,7.9 18.84,9.13L15.8,10.68C15.5,10.16 15,9.76 14.4,9.54L14.95,7.5C14,7.19 13,7 12,7C11.5,7 11,7.06 10.5,7.18L11.19,9.45C10.6,9.65 10.08,10.03 9.68,10.54L6.6,9.05C7.14,7.85 8,6.83 9.12,6.17L9.92,8.83C8.94,9.33 8.16,10.2 7.84,11.23L4.05,12.58C4,12.4 4,12.2 4,12A8,8 0 0,1 12,4M12,20A8,8 0 0,1 4,12C4,11.8 4,11.61 4.05,11.42L6.4,12.77C6.73,13.78 7.5,14.64 8.47,15.13L7.67,17.8C6.56,17.12 5.7,16.1 5.16,14.87L8.2,13.32C8.5,13.84 9,14.24 9.6,14.46L9.05,16.5C10,16.81 11,17 12,17C12.5,17 13,16.94 13.5,16.82L12.81,14.55C13.4,14.35 13.92,13.97 14.32,13.46L17.4,14.95C16.86,16.15 16,17.17 14.88,17.83L14.08,15.17C15.06,14.67 15.84,13.8 16.16,12.77L19.95,11.42C20,11.61 20,11.8 20,12A8,8 0 0,1 12,20Z" /></svg>` });
    }

    if (features.length === 0) return;

    // --- Create Icon Toolbar ---
    const toolbarWrapper = document.createElement('div');
    toolbarWrapper.className = 'specifikit-toolbar-wrapper';
    const iconToolbar = document.createElement('div');
    iconToolbar.className = 'specifikit-icon-toolbar';
    if (this.featureUiConfig.icon_toolbar_transparent_bg) {
        iconToolbar.dataset.transparent = 'true';
    }
    iconToolbar.dataset.shape = this.featureUiConfig.icon_toolbar_button_shape || 'rounded';
    iconToolbar.dataset.align = this.featureUiConfig.icon_toolbar_alignment || 'center';

    features.forEach(feature => {
        const btn = document.createElement('button');
        btn.className = `specifikit-icon-btn specifikit-btn-${feature.id}-config`;
        btn.setAttribute('aria-label', feature.text);
        btn.dataset.tooltip = feature.text;
        btn.innerHTML = feature.icon;
        btn.addEventListener('click', feature.action);
        iconToolbar.appendChild(btn);
    });
    toolbarWrapper.appendChild(iconToolbar);
    wrapper.appendChild(toolbarWrapper);

    // --- Create Text Buttons ---
    const textButtonsContainer = document.createElement('div');
    const layout = this.featureUiConfig.text_button_layout_desktop || 'default_split';
    textButtonsContainer.className = 'specifikit-text-buttons';
    textButtonsContainer.dataset.layout = layout;
    
    if (layout === 'default_split' && features.some(f => f.id === 'compare')) {
        const compareBtnFeature = features.find(f => f.id === 'compare');
        const otherFeatures = features.filter(f => f.id !== 'compare');
    
        if (compareBtnFeature) {
            const compareWrapper = document.createElement('div');
            compareWrapper.className = 'specifikit-text-buttons-compare-wrapper';
            const btn = this._createTextButton(compareBtnFeature);
            compareWrapper.appendChild(btn);
            textButtonsContainer.appendChild(compareWrapper);
        }
    
        if (otherFeatures.length > 0) {
            const othersWrapper = document.createElement('div');
            othersWrapper.className = 'specifikit-text-buttons-others-wrapper';
            otherFeatures.forEach(feature => {
                const btn = this._createTextButton(feature);
                othersWrapper.appendChild(btn);
            });
            textButtonsContainer.appendChild(othersWrapper);
        }
    } else {
        // Fallback for 'inline', 'full_width', or if 'compare' button doesn't exist
        features.forEach(feature => {
            const btn = this._createTextButton(feature);
            textButtonsContainer.appendChild(btn);
        });
    }
    
    wrapper.appendChild(textButtonsContainer);
    wrapper.dataset.uiRendered = 'true';
  }

  _createTextButton(feature) {
    const btn = document.createElement('button');
    btn.className = `specifikit-btn specifikit-btn-md specifikit-btn-advanced-text specifikit-btn-${feature.id}-config`;
    btn.innerHTML = `${feature.icon} <span class="specifikit-btn-advanced-text-label">${feature.text}</span>`;
    btn.addEventListener('click', feature.action);
    return btn;
  }

  // ==========================================
  // FEATURE: Image Zoom
  // ==========================================
  setupImageZoom(scope) {
    this.logger.log('🔍 Setting up image zoom functionality for scope:', scope);
    
    const imageContainer = scope.querySelector('[data-specifikit-image-container]');
    if (!imageContainer) {
      this.logger.warn('Zoom: No image container found in scope');
      return;
    }
    
    if (imageContainer.dataset.zoomFunctionalityInitialized === 'true') {
      this.logger.log('Zoom: Functionality already initialized, skipping');
      return;
    }

    // Attach click listener to the dedicated zoom button
    const btn = scope.querySelector('.specifikit-view-zoom-button');
    if (btn) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openLightbox();
      });
      this.logger.log('Zoom: Attaching click listener to zoom button.');
    }

    // Attach click-to-zoom listener directly on the image as well
    imageContainer.addEventListener('click', (e) => {
      if (e.target.closest('button')) return;
      if (!this.state.view360Active) this.openLightbox();
    });

    imageContainer.style.cursor = 'zoom-in';
    this.setupHoverMagnifier(imageContainer);
    imageContainer.dataset.zoomFunctionalityInitialized = 'true';
    
    this.logger.info('✅ Zoom functionality attached successfully');
  }

  setupHoverMagnifier(imageContainer) {
    if (!imageContainer) return;
    
    let magnifier = document.getElementById('specifikit-image-magnifier-singleton');
    if (!magnifier) {
      magnifier = document.createElement('div');
      magnifier.id = 'specifikit-image-magnifier-singleton';
      magnifier.className = 'specifikit-image-magnifier';
      document.body.appendChild(magnifier);
    }

    const handleMouseMove = (e) => {
      const activeImage = imageContainer.querySelector('img:not([style*="display: none"])');
      // =================================================================
      // Also check for isInitialState before showing the magnifier.
      // =================================================================
      if (this.state.view360Active || !activeImage || this.customizer.state.isInitialState) {
        magnifier.style.display = 'none';
        return;
      }

      magnifier.style.display = 'block';
      const rect = imageContainer.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      magnifier.style.left = `${e.clientX + 20}px`;
      magnifier.style.top = `${e.clientY + 20}px`;
      magnifier.style.backgroundImage = `url(${activeImage.src})`;
      magnifier.style.backgroundPosition = `${(x / rect.width) * 100}% ${(y / rect.height) * 100}%`;
      magnifier.style.backgroundSize = '250%';
    };

    const handleMouseEnter = () => {
      if (imageContainer.closest('.specifikit-modal-view')) {
        magnifier.style.zIndex = '10001';
      } else {
        magnifier.style.zIndex = '1000';
      }
      // =================================================================
      // Also check for isInitialState before showing the magnifier.
      // =================================================================
      if (!this.state.view360Active && !this.customizer.state.isInitialState) {
        magnifier.style.display = 'block';
      }

    };
    
    imageContainer.addEventListener('mouseenter', handleMouseEnter);
    imageContainer.addEventListener('mouseleave', () => magnifier.style.display = 'none');
    imageContainer.addEventListener('mousemove', handleMouseMove);
  }

  openLightbox() {
    const scope = this.customizer.ui.modal.isOpen ? this.customizer.ui.modal.instance : this.container;
    const activeImage = scope.querySelector('img:not([style*="display: none"])');
    const currentImageSrc = activeImage?.src;
    if (!currentImageSrc) return;
    
    // Use the themed modal creator
    const { modalElement: lightboxElement, fragment: lightboxFragment } = this._createThemedModalFromTemplate('specifikit-lightbox-template', '.specifikit-customizer-lightbox');
    if (!lightboxElement) return;

    const state = this.customizer.state;
    const filenameParts = [this.config.productHandle, state.size];
    Object.values(state.componentSelections).forEach(sel => {
      if(Array.isArray(sel)) { sel.forEach(s => s.handle && filenameParts.push(s.handle)); }
      else if (sel && sel.handle) { filenameParts.push(sel.handle); }
    });
    const downloadFilename = `${filenameParts.join('-')}.jpg`;
    
    if (this.customizer.ui.modal.isOpen) {
      lightboxElement.classList.add('in-modal-context');
    }

    const img = lightboxElement.querySelector('[data-specifikit-template-img]');
    img.src = currentImageSrc;
    img.alt = `Zoomed view of ${this.config.productHandle || 'custom product'}`;

    lightboxElement.querySelector('.specifikit-lightbox-close').ariaLabel = this.i18n.zoom.lightbox_close;
    lightboxElement.querySelector('.specifikit-lightbox-zoom-in').ariaLabel = this.i18n.zoom.lightbox_zoom_in;
    lightboxElement.querySelector('.specifikit-lightbox-zoom-out').ariaLabel = this.i18n.zoom.lightbox_zoom_out;
    const downloadSpan = lightboxElement.querySelector('.specifikit-lightbox-download span');
    if(downloadSpan) downloadSpan.textContent = this.i18n.zoom.lightbox_download;

    document.body.appendChild(lightboxFragment);

    if (!this.customizer.ui.modal.isOpen) {
      document.body.style.overflow = 'hidden';
    }

    let scale = 1, isDragging = false, startPos = { x: 0, y: 0 }, translate = { x: 0, y: 0 };
    img.style.transformOrigin = 'center center';
    
    const updateTransform = () => {
      img.style.transform = `translate(${translate.x}px, ${translate.y}px) scale(${scale})`;
    };

    const onMouseMove = (e) => {
      if (!isDragging) return;
      translate.x = e.clientX - startPos.x;
      translate.y = e.clientY - startPos.y;
      updateTransform();
    };

    const onMouseUp = () => {
      isDragging = false;
      img.classList.remove('is-dragging');
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    img.addEventListener('dragstart', (e) => e.preventDefault());

    img.addEventListener('mousedown', (e) => {
      if (scale <= 1) return;
      e.preventDefault();
      isDragging = true;
      startPos = { x: e.clientX - translate.x, y: e.clientY - translate.y };
      img.classList.add('is-dragging');
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    });

    lightboxElement.querySelector('.specifikit-lightbox-zoom-in').addEventListener('click', () => {
      scale = Math.min(scale + 0.5, 3);
      img.classList.toggle('is-draggable', scale > 1);
      updateTransform();
    });

    lightboxElement.querySelector('.specifikit-lightbox-zoom-out').addEventListener('click', () => {
      scale = Math.max(scale - 0.5, 1);
      if (scale === 1) { 
        translate = { x: 0, y: 0 };
      }
      img.classList.toggle('is-draggable', scale > 1);
      updateTransform();
    });

    const downloadLink = lightboxElement.querySelector('[data-specifikit-template-download]');
    downloadLink.addEventListener('click', async (e) => {
      e.preventDefault();
      const href = img.src;
      downloadLink.classList.add('is-loading');
      try {
        const response = await fetch(href);
        if (!response.ok) throw new Error('Network response was not ok.');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = downloadFilename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } catch (error) {
        this.logger.error('Download Failed:', error);
        window.open(href, '_blank');
      } finally {
        downloadLink.classList.remove('is-loading');
      }
    });
    
    const close = () => {
      if (!this.customizer.ui.modal.isOpen) {
        document.body.style.overflow = '';
      }
      lightboxElement.remove();
      document.removeEventListener('keydown', escHandler);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    lightboxElement.querySelector('.specifikit-lightbox-close').addEventListener('click', close);
    lightboxElement.querySelector('.specifikit-lightbox-overlay').addEventListener('click', close);

    const escHandler = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', escHandler);
  }

  // ==========================================
  // FEATURE: 360° View
  // ==========================================
  setup360View(scope) {
    this.logger.log('🔄 Setting up 360° view functionality...');
    const btn = scope.querySelector('.specifikit-view-360-button');
    if (btn) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggle360View();
      });
      this.logger.log('360 View: Attached click listener to button.');
    } else {
      this.logger.log('360 View: Button not found in this scope.');
    }

    if (!this.view360InteractionSetup) {
      this.setup360Interaction();
      this.view360InteractionSetup = true;
    }
  }

  setup360Interaction() {
    let isDragging = false, startX = 0, currentFrame = 0;
    const totalFrames = this.config.threeSixtyViewConfig?.total_frames || 36;
    let activeContainer = null;

    const startDrag = (clientX, imageElement, container) => {
      if (!this.state.view360Active || !imageElement) return;
      isDragging = true;
      startX = clientX;
      activeContainer = container;
      imageElement.style.cursor = 'grabbing';
    };

    const drag = (clientX) => {
      if (!isDragging || !activeContainer) return;
      const imageElement = activeContainer.querySelector('[data-specifikit-image-simple]');
      if (!imageElement) return;
      
      const deltaX = clientX - startX;
      const sensitivity = 5;
      if (Math.abs(deltaX) > sensitivity) {
        const direction = deltaX > 0 ? -1 : 1;
        currentFrame = (currentFrame + direction + totalFrames) % totalFrames;
        this.update360Image(currentFrame, activeContainer);
        startX = clientX;
      }
    };

    const endDrag = () => {
      if (isDragging && activeContainer) {
        const imageElement = activeContainer.querySelector('[data-specifikit-image-simple]');
        if (this.state.view360Active && imageElement) {
          imageElement.style.cursor = 'grab';
        }
      }
      isDragging = false;
      activeContainer = null;
    };

    // UNIVERSAL EVENT LISTENERS
    document.addEventListener('mousedown', (e) => {
      const container = e.target.closest('[data-specifikit-image-container]');
      if (!container) return;
      
      const imageElement = container.querySelector('[data-specifikit-image-simple]');
      if (e.target === imageElement) {
        startDrag(e.clientX, imageElement, container);
      }
    });
    
    document.addEventListener('mousemove', (e) => {
      drag(e.clientX);
    });
    
    document.addEventListener('mouseup', () => {
      endDrag();
    });
    
    document.addEventListener('touchstart', (e) => {
      const container = e.target.closest('[data-specifikit-image-container]');
      if (!container) return;
      
      const imageElement = container.querySelector('[data-specifikit-image-simple]');
      if (e.target === imageElement) {
        startDrag(e.touches[0].clientX, imageElement, container);
      }
    }, { passive: true });
    
    document.addEventListener('touchmove', (e) => {
      if (isDragging && e.touches[0]) {
        drag(e.touches[0].clientX);
      }
    }, { passive: true });
    
    document.addEventListener('touchend', () => {
      endDrag();
    });
    
    this.logger.info('✅ 360 View: Universal interaction handlers initialized');
  }

  toggle360View() {
    // This is the single entry point for toggling the 360 view state.
    if (this.state.view360Active) {
        this.deactivate360View();
    } else {
        this.activate360View();
    }
  }

  activate360View() {
    this.state.view360Active = true;
    this.logger.log('✅ 360 View activated');

    const scope = this.customizer.ui.modal.isOpen && this.customizer.ui.modal.instance 
      ? this.customizer.ui.modal.instance 
      : this.container;
      
    if (!scope) return;

    const btn = scope.querySelector('.specifikit-view-360-button');
    const imageElement = scope.querySelector('[data-specifikit-image-simple]');
    const multiAngleButtons = scope.querySelectorAll('.specifikit-view-button[data-view]');

    if (btn) {
      btn.classList.add('specifikit-active');
      btn.querySelector('span').textContent = this.i18n.three_sixty.button_photo;
    }
    if (imageElement) imageElement.style.cursor = 'grab';

    this.customizer.ui.deactivateAllViewButtons();
    multiAngleButtons.forEach(b => b.style.display = 'none');

    this.update360Image(0, scope);
    this.showNotification(this.i18n.three_sixty.notification, 'info');
  }

  deactivate360View(force = false) {
    if (!this.state.view360Active && !force) return;

    this.state.view360Active = false;
    this.logger.log('✅ 360 View deactivated');

    // Use a universal selector to catch buttons in both page and modal contexts
    document.querySelectorAll('.specifikit-view-360-button').forEach(btn => {
        btn.classList.remove('specifikit-active');
        const textSpan = btn.querySelector('span');
        if (textSpan) textSpan.textContent = this.i18n.three_sixty.button_360;
    });

    document.querySelectorAll('.specifikit-view-button[data-view]').forEach(b => b.style.display = '');

    document.querySelectorAll('[data-specifikit-image-simple]').forEach(img => {
        img.style.cursor = this.featureFlags.enable_image_zoom ? 'zoom-in' : 'default';
    });

    this.customizer.ui.reactivateCurrentViewButton();
    
    // If we are deactivating because a static view was clicked (force=true), the core handles the image update.
    // If we are deactivating by clicking the 360 button itself, we need to trigger the update.
    if (!force) {
      this.customizer.ui.updateImage();
    }
  }

  async update360Image(frame, targetContainer) {
    const config = this.config.threeSixtyViewConfig;
    
    const scope = targetContainer || (this.customizer.ui.modal.isOpen && this.customizer.ui.modal.instance 
        ? this.customizer.ui.modal.instance 
        : this.container);
    
    const imageElement = scope?.querySelector('[data-specifikit-image-simple]');
    
    if (!config || !config.enable || !config.template || !imageElement) {
      this.logger.error('360 View: Missing config, template, or image element');
      return;
    }

    const currentState = { size: this.customizer.state.size, ...this.customizer.state.componentSelections };
    let imageUrl = config.template;

    imageUrl = imageUrl.replace('{size}', currentState.size || '');
    for (const [key, selection] of Object.entries(currentState)) {
      if (selection && selection.handle) imageUrl = imageUrl.replace(`{${key}}`, selection.handle);
    }
    imageUrl = imageUrl.replace('{frame_number}', String(frame).padStart(2, '0'));

    this.customizer.ui.showSpinner(true);
    try {
      await this.customizer.loadImage(imageUrl);
      imageElement.src = imageUrl;
    } catch (error) {
      this.logger.warn('360° frame load failed:', frame, error);
      this.toggle360View(); // Attempt to gracefully exit 360 mode
      this.showNotification('360° view is unavailable for this configuration.', 'error');
    } finally {
      this.customizer.ui.showSpinner(false);
    }
  }

  // ==========================================
  // FEATURE: AR Preview
  // ==========================================
  setupARPreview(scope) {
    const btn = scope.querySelector('.specifikit-view-ar-button');
    if (btn) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.launchARPreview();
      });
      this.logger.info('✅ AR Preview: Functionality attached to button.');
    } else {
      this.logger.log('AR Preview: Button not found in this scope, skipping setup.');
    }
  }

  async launchARPreview() {
    this.showNotification(this.i18n.ar.loading, 'info');

    try {
      await this.loadModelViewerScript();
    } catch (error) {
      this.logger.error('Failed to load <model-viewer> script:', error);
      this.showNotification(this.i18n.ar.error_library, 'error');
      return;
    }
    
    const { modalElement, fragment: modalFragment } = this._createThemedModalFromTemplate('specifikit-ar-modal-template', '.specifikit-utility-modal');
    if (!modalElement) return;

    const scope = this.customizer.ui.modal.isOpen ? this.customizer.ui.modal.instance : this.container;
    const activeImage = scope.querySelector('img:not([style*="display: none"])');
    const arConfig = this.config.arConfig;
    
    this.populateTemplateText(modalElement, {
      '[data-specifikit-template-title]': this.i18n.ar.modal_title,
      '[data-specifikit-template-status]': this.i18n.ar.model_viewer_status,
      '[data-specifikit-template-launch-button] span': this.i18n.ar.button,
      '[data-specifikit-template-error-title]': this.i18n.ar.model_viewer_error,
      '[data-specifikit-template-error-detail]': this.i18n.ar.model_viewer_error_detail,
      '[data-specifikit-template-not-supported-title]': this.i18n.ar.not_supported_title,
      '[data-specifikit-template-not-supported-message]': this.i18n.ar.not_supported_message,
    });
    
    const close = () => {
      modalElement.remove();
      if (!this.customizer.ui.modal.isOpen) document.body.style.overflow = '';
    };

    const modelViewer = modalElement.querySelector('model-viewer');
    modelViewer.setAttribute('src', arConfig.model_glb);
    modelViewer.setAttribute('ios-src', arConfig.model_usdz);
    modelViewer.setAttribute('poster', activeImage?.src || '');
    
    document.body.appendChild(modalFragment);
    if (!this.customizer.ui.modal.isOpen) document.body.style.overflow = 'hidden';

    modalElement.querySelector('.specifikit-ar-close').addEventListener('click', close);
    modalElement.querySelector('.specifikit-ar-overlay').addEventListener('click', close);
    
    modelViewer.addEventListener('error', (event) => {
      this.logger.error('Model-viewer error:', event.detail);
      const errorDiv = modalElement.querySelector('[data-specifikit-template-error]');
      const statusDiv = modalElement.querySelector('.specifikit-ar-status');
      if (errorDiv) errorDiv.style.display = 'flex';
      if (statusDiv) statusDiv.style.display = 'none';
    });

    modelViewer.addEventListener('ar-status', (event) => {
      if (event.detail.status === 'not-presenting' && !modelViewer.canActivateAR) {
        const notSupportedDiv = modalElement.querySelector('[data-specifikit-template-not-supported]');
        if(notSupportedDiv) notSupportedDiv.style.display = 'block';
      }
    });
  }

  // ==========================================
  // FEATURE: Save & Share
  // ==========================================
  setupSaveShare(scope) {
    // This function's UI logic is now handled by _setupAdvancedUI.
    // It remains in the call stack for potential future logic expansion.
  }

  showSaveDialog() {
    const defaultName = `${this.config.productTitle} - ${this.formatDate(new Date())}`;
    const { modalElement: dialog, fragment: dialogFragment } = this._createThemedModalFromTemplate('specifikit-save-dialog-template', '.specifikit-utility-modal');
    if (!dialog) return;
    
    const titleEl = dialog.querySelector('[data-specifikit-template-title]');
    const promptEl = dialog.querySelector('[data-specifikit-template-prompt]');
    const confirmBtn = dialog.querySelector('[data-specifikit-template-confirm-btn]');
    const inputEl = dialog.querySelector('[data-specifikit-template-input]');

    if (titleEl) titleEl.textContent = this.i18n.save_share.save_button + ' Design';
    if (promptEl) promptEl.textContent = this.i18n.save_share.save_prompt;
    if (confirmBtn) confirmBtn.textContent = this.i18n.save_share.save_button;

    if (!inputEl) {
      this.logger.error("Save dialog missing input element.");
      return;
    }

    document.body.appendChild(dialogFragment);
    
    inputEl.value = defaultName;
    inputEl.focus();
    inputEl.select();

    const close = () => dialog.remove();

    dialog.querySelector('.specifikit-dialog-overlay')?.addEventListener('click', close);
    dialog.querySelector('[data-specifikit-template-cancel-btn]')?.addEventListener('click', close);
    confirmBtn?.addEventListener('click', () => {
      const name = inputEl.value.trim();
      if (name) {
        this.saveConfiguration(name);
        close();
      } else {
        inputEl.style.borderColor = '#ef4444';
      }
    });
  }

  saveConfiguration(name) {
    if (!name) return;
    
    const scope = this.customizer.ui.modal.isOpen ? this.customizer.ui.modal.instance : this.container;
    const activeImage = scope.querySelector('img:not([style*="display: none"])');
    const config = {
      id: Date.now(),
      name: name,
      timestamp: new Date().toISOString(),
      state: JSON.parse(JSON.stringify(this.customizer.state)),
      price: this.customizer.getTotalPrice(),
      imageUrl: activeImage?.src || ''
    };

    this.state.savedConfigs.push(config);
    this.saveConfigsToStorage();
    this.showNotification(this.i18n.save_share.save_success, 'success');
  }

  loadSavedConfigsFromStorage() {
    try {
      const key = `specifikit_saved_configs_${this.config.productId}`;
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      this.logger.error('Failed to load saved configurations:', e);
      return [];
    }
  }

  saveConfigsToStorage() {
    try {
      const key = `specifikit_saved_configs_${this.config.productId}`;
      localStorage.setItem(key, JSON.stringify(this.state.savedConfigs));
    } catch (e) {
      this.logger.error('Failed to save configurations:', e);
      this.showNotification('Could not save configuration. Your browser storage might be full.', 'error');
    }
  }

  showLoadModal() {
    const { modalElement, fragment: modalFragment } = this._createThemedModalFromTemplate('specifikit-load-modal-template', '.specifikit-utility-modal');
    if (!modalElement) return;

    const listContainer = modalElement.querySelector('[data-specifikit-template-list-container]');
    const titleEl = modalElement.querySelector('h3');
    const closeBtn = modalElement.querySelector('.specifikit-modal-close');

    if (!listContainer) return;

    if (titleEl) titleEl.textContent = this.i18n.save_share.load_modal_title;
    if (closeBtn) closeBtn.textContent = this.i18n.zoom.lightbox_close;

    if (this.state.savedConfigs.length === 0) {
      const emptyTemplate = document.getElementById('specifikit-load-modal-empty-template');
      if (emptyTemplate) {
        const emptyFragment = emptyTemplate.content.cloneNode(true);
        const titleElement = emptyFragment.querySelector('[data-specifikit-template-empty-title]');
        if (titleElement) {
          const titleText = this.i18n.save_share.no_saved_configs;
          titleElement.textContent = (titleText && titleText.trim()) ? titleText : 'You have no saved configurations yet.';
        }
        const promptEl = emptyFragment.querySelector('[data-specifikit-template-empty-prompt]');
        if (promptEl) {
          let promptHTML = this.i18n.save_share.no_saved_configs_prompt;
          const defaultPromptHTML = "Click the '<strong>Save</strong>' button to store your favorite designs here for later.";
          if (!promptHTML || !promptHTML.trim()) {
            promptHTML = defaultPromptHTML;
          } else {
            promptHTML = promptHTML.replace(/<strong>(['"])([\w\s]+)\1<\/strong>/g, "$1<strong>$2</strong>$1");
          }
          promptEl.innerHTML = this.unescapeHtml(promptHTML);
        }
        listContainer.appendChild(emptyFragment);
      } else {
        listContainer.textContent = this.i18n.save_share.no_saved_configs;
      }
    } else {
      const itemTemplate = document.getElementById('specifikit-load-modal-item-template');
      if (!itemTemplate) {
        this.logger.error('Advanced: Load modal item template not found.');
        return;
      }
      this.state.savedConfigs.forEach(config => {
        const itemFragment = itemTemplate.content.cloneNode(true);
        const itemElement = itemFragment.querySelector('.specifikit-saved-config-item');
        
        const state = config.state || {};
        const descParts = [state.size ? this.customizer.capitalize(state.size) : ''];
        Object.values(state.componentSelections || {}).forEach(sel => {
          if (Array.isArray(sel)) { sel.forEach(s => s.name && descParts.push(s.name)); }
          else if (sel && sel.name) { descParts.push(sel.name); }
        });
        const description = this.customizer.ui.escapeHtml(descParts.filter(Boolean).join(' | '));

        const date = new Date(config.timestamp);
        const formattedTimestamp = new Intl.DateTimeFormat(undefined, {
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }).format(date);

        itemElement.dataset.configId = config.id;
        itemElement.querySelector('[data-specifikit-template-img]').src = config.imageUrl;
        itemElement.querySelector('[data-specifikit-template-img]').alt = this.customizer.ui.escapeHtml(config.name);
        itemElement.querySelector('[data-specifikit-template-name]').textContent = this.customizer.ui.escapeHtml(config.name);
        itemElement.querySelector('[data-specifikit-template-description]').textContent = description;
        itemElement.querySelector('[data-specifikit-template-price]').textContent = this.customizer.formatMoney(config.price * 100);
        
        const timestampEl = itemElement.querySelector('[data-specifikit-template-timestamp]');
        timestampEl.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z" /></svg>
          <span>${formattedTimestamp}</span>
        `;
        
        const loadBtn = itemElement.querySelector('.specifikit-btn-load');
        loadBtn.dataset.id = config.id;
        loadBtn.textContent = this.i18n.save_share.load_this_button;

        const deleteBtn = itemElement.querySelector('.specifikit-btn-delete');
        deleteBtn.dataset.id = config.id;
        deleteBtn.dataset.name = config.name;
        deleteBtn.textContent = this.i18n.save_share.delete_button;

        listContainer.appendChild(itemFragment);
      });
    }
    
    document.body.appendChild(modalFragment);

    const close = () => modalElement.remove();
    modalElement.querySelector('.specifikit-modal-overlay').addEventListener('click', close);
    modalElement.querySelector('.specifikit-modal-close').addEventListener('click', close);

    modalElement.addEventListener('click', async (e) => {
      const loadBtn = e.target.closest('.specifikit-btn-load');
      const deleteBtn = e.target.closest('.specifikit-btn-delete');

      if (loadBtn) {
        const configId = parseInt(loadBtn.dataset.id, 10);
        const cfg = this.state.savedConfigs.find((c) => c.id === configId);
        if (cfg) this.applyConfiguration(cfg);
        close();
      } else if (deleteBtn) {
        const configId = parseInt(deleteBtn.dataset.id, 10);
        const configName = deleteBtn.dataset.name;
        
        const confirmed = await this.showConfirmDialog({
          title: this.i18n.save_share.delete_confirm_title,
          prompt: this.i18n.save_share.delete_confirm_prompt.replace('{configName}', this.escapeHtml(configName)),
          confirmText: this.i18n.save_share.delete_confirm_button,
          cancelText: this.i18n.save_share.delete_cancel_button,
          isDestructive: true
        });

        if (confirmed) {
          this.deleteConfiguration(configId);
          const item = modalElement.querySelector(`.specifikit-saved-config-item[data-config-id="${configId}"]`);
          if (item) item.remove();
          if (this.state.savedConfigs.length === 0) {
            this.showLoadModal();
            close();
          }
        }
      }
    });
  }

  applyConfiguration(config) {
    if (!config || !config.state) {
      this.logger.error("Advanced: Invalid config for application.");
      this.showNotification('Could not load configuration. The data is invalid.', 'error');
      return;
    }
    
    const report = this.customizer.applyConfiguration(config.state);
    let baseMessage = `Loaded "${this.escapeHtml(config.name || 'design')}"`;
    
    if (report.failed.length > 0) {
      const failedCount = report.failed.length;
      this.logger.warn(`Failed to apply ${failedCount} option(s):`, report.failed);
      let notificationMessage = `${baseMessage}, but ${failedCount} option${failedCount > 1 ? 's were' : ' was'} unavailable and has been reset.`;
      this.showNotification(notificationMessage, 'info');
    } else {
      this.showNotification(`${baseMessage}!`, 'success');
    }
  }

  deleteConfiguration(configId) {
    this.state.savedConfigs = this.state.savedConfigs.filter((c) => c.id !== configId);
    this.saveConfigsToStorage();
    this.showNotification(this.i18n.save_share.deleted_success, 'info');
  }

  shareConfiguration() {
    try {
      const encoded = btoa(JSON.stringify(this.customizer.state));
      const shareUrl = `${window.location.origin}${window.location.pathname}?config=${encoded}`;
      this.showShareDialog(shareUrl);
    } catch(e) {
      this.logger.error("Error creating share link:", e);
      this.showNotification('Could not create share link.', 'error');
    }
  }

  showShareDialog(url) {
    const { modalElement: dialogElement, fragment: dialogFragment } = this._createThemedModalFromTemplate('specifikit-share-dialog-template', '.specifikit-utility-modal');
    if (!dialogElement) return;

    dialogElement.querySelector('h3').textContent = this.i18n.save_share.share_modal_title;
    dialogElement.querySelector('p').textContent = this.i18n.save_share.share_modal_prompt;
    dialogElement.querySelector('.specifikit-dialog-close').textContent = this.i18n.save_share.share_done;
    
    const emailLink = dialogElement.querySelector('.specifikit-share-email');
    emailLink.href = `mailto:?subject=${encodeURIComponent('Check out my custom design')}&body=${encodeURIComponent('I created this design, check it out: ' + url)}`;
    emailLink.innerHTML = emailLink.innerHTML.replace('Email', this.i18n.save_share.share_email);

    const whatsappLink = dialogElement.querySelector('.specifikit-share-whatsapp');
    whatsappLink.href = `https://wa.me/?text=${encodeURIComponent('Check out my design: ' + url)}`;
    whatsappLink.innerHTML = whatsappLink.innerHTML.replace('WhatsApp', this.i18n.save_share.share_whatsapp);
    
    const urlInput = dialogElement.querySelector('[data-specifikit-template-url-input]');
    urlInput.value = url;
    
    const copyButton = dialogElement.querySelector('[data-specifikit-template-copy-btn]');
    
    document.body.appendChild(dialogFragment);
    
    const close = () => dialogElement.remove();
    dialogElement.querySelector('.specifikit-dialog-overlay').addEventListener('click', close);
    dialogElement.querySelector('.specifikit-dialog-close').addEventListener('click', close);

    copyButton.addEventListener('click', () => this.copyShareLink(urlInput, copyButton));
    urlInput.addEventListener('click', () => urlInput.select());
  }
  
  copyShareLink(inputElement, buttonElement) {
    if (buttonElement.dataset.isCopied === 'true') return;

    const originalText = buttonElement.textContent;
    
    const copyPromise = navigator.clipboard ? 
      navigator.clipboard.writeText(inputElement.value) :
      new Promise((resolve, reject) => {
        try {
          inputElement.select();
          document.execCommand('copy') ? resolve() : reject();
        } catch (e) { reject(e); }
      });

    copyPromise.then(() => {
      this.showNotification(this.i18n.save_share.link_copied, 'success');
      buttonElement.textContent = 'Copied!';
      buttonElement.dataset.isCopied = 'true';
      setTimeout(() => { 
        buttonElement.textContent = originalText;
        delete buttonElement.dataset.isCopied;
      }, 2500);
    }).catch(() => {
      this.showNotification('Could not copy link automatically. Please copy it manually.', 'info');
    });
  }

  setupURLConfigLoader() {
    const urlParams = new URLSearchParams(window.location.search);
    const configParam = urlParams.get('config');
    if (!configParam) return;

    try {
      const decodedState = JSON.parse(atob(configParam));
      const report = this.customizer.applyConfiguration(decodedState);
      
      if (report.failed.length > 0) {
        this.showNotification('Shared configuration loaded, but some options were unavailable and have been reset.', 'info');
      } else {
        this.showNotification('Shared configuration loaded successfully!', 'success');
      }

      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } catch (e) {
      this.logger.error('Invalid config in URL:', e);
      this.showNotification('Could not apply the shared configuration. It may be invalid or from an older version.', 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  // ==========================================
  // FEATURE: Price Breakdown
  // ==========================================
  setupPriceBreakdown(scope) {
    const placeholder = scope.querySelector('[data-specifikit-placeholder="price-breakdown"]');
    if (!placeholder) {
      this.logger.warn('Advanced: Price breakdown placeholder not found in scope');
      return;
    }
    if (placeholder.querySelector('.specifikit-price-breakdown')) {
      this.logger.log('Price Breakdown: Already exists in this placeholder');
      return;
    }

    const wrapperEnabled = this.featureFlags.enable_price_breakdown_wrapper;

    const container = document.createElement('div');
    container.className = 'specifikit-price-breakdown';

    const toggle = document.createElement('div');
    toggle.className = 'specifikit-breakdown-toggle';
    toggle.setAttribute('role', 'button');
    toggle.tabIndex = 0;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', 'specifikit-breakdown-details-panel');
    toggle.innerHTML = `
      <div class="specifikit-toggle-icon-wrapper">
        <svg class="specifikit-toggle-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <span class="specifikit-toggle-text">${this.i18n.price_breakdown.title}</span>
    `;

    const details = document.createElement('div');
    details.id = 'specifikit-breakdown-details-panel';
    details.className = 'specifikit-breakdown-details';
    details.innerHTML = `
      <div class="specifikit-breakdown-content-wrapper">
        <div class="specifikit-breakdown-item">
          <span class="specifikit-label">${this.i18n.price_breakdown.base_price}</span>
          <span id="specifikit-breakdown-base"></span>
        </div>
        <div id="specifikit-breakdown-components"></div>
        <div id="specifikit-breakdown-addons"></div>
        <div class="specifikit-breakdown-item specifikit-breakdown-total">
          <strong>${this.i18n.price_breakdown.total}</strong>
          <strong id="specifikit-breakdown-total"></strong>
        </div>
      </div>
    `;

    if (wrapperEnabled) {
      const wrapper = document.createElement('div');
      wrapper.className = 'specifikit-price-breakdown-wrapper';
      wrapper.appendChild(toggle);
      wrapper.appendChild(details);
      container.appendChild(wrapper);
    } else {
      container.appendChild(toggle);
      container.appendChild(details);
    }
    
    placeholder.appendChild(container);

    toggle.addEventListener('click', () => {
      const isOpening = !toggle.classList.contains('specifikit-active');
      toggle.classList.toggle('specifikit-active');
      toggle.setAttribute('aria-expanded', isOpening);
      details.classList.toggle('specifikit-active');
    });
    toggle.addEventListener('keydown', (e) => { 
      if (e.key === 'Enter' || e.key === ' ') { 
        e.preventDefault(); 
        toggle.click(); 
      }
    });

    const initialDetail = {
      state: this.customizer.state,
      totalPrice: this.customizer.getTotalPrice()
    };
    this.updatePriceBreakdown(initialDetail);
  }
  
  updatePriceBreakdown(detail) {
    const activeScope = this.customizer.ui.modal.isOpen && this.customizer.ui.modal.instance ? this.customizer.ui.modal.instance : this.container;
    const container = activeScope.querySelector('.specifikit-price-breakdown');
    
    if (!container || !detail || !detail.state) {
      return;
    }

    const { state, totalPrice } = detail;
    let hasExtraCosts = false;
    
    container.querySelector('#specifikit-breakdown-base').textContent = this.customizer.formatMoney(state.basePrice * 100);

    const componentsDiv = container.querySelector('#specifikit-breakdown-components');
    componentsDiv.innerHTML = '';
    
    Object.values(state.componentSelections).forEach(selection => {
      if (Array.isArray(selection)) {
        selection.forEach(sub => {
          if (sub.priceModifier > 0) {
            componentsDiv.appendChild(this.createBreakdownItem(sub.name, sub.priceModifier));
            hasExtraCosts = true;
          }
        });
      } else if (selection && selection.priceModifier > 0) {
        componentsDiv.appendChild(this.createBreakdownItem(selection.name, selection.priceModifier));
        hasExtraCosts = true;
      }
    });

    const addonsDiv = container.querySelector('#specifikit-breakdown-addons');
    addonsDiv.innerHTML = '';
    
    Object.values(state.addons).forEach(addon => {
      if ((addon.type === 'checkbox' && addon.enabled && addon.price > 0) || (addon.type === 'text' && addon.value && addon.price > 0)) {
        addonsDiv.appendChild(this.createBreakdownItem(addon.label, addon.price));
        hasExtraCosts = true;
      }
    });

    container.querySelector('#specifikit-breakdown-total').textContent = this.customizer.formatMoney(totalPrice * 100);

    if (this.featureFlags.enable_smart_price_breakdown) {
      container.style.display = hasExtraCosts ? 'block' : 'none';
    } else {
      container.style.display = 'block';
    }
  }

  createBreakdownItem(label, price) {
    const item = document.createElement('div');
    item.className = 'specifikit-breakdown-item';
    item.innerHTML = `
      <span class="specifikit-label">${this.escapeHtml(label)}</span>
      <span>+${this.customizer.formatMoney(price * 100)}</span>
    `;
    return item;
  }

  // ==========================================
  // FEATURE: Comparison Mode
  // ==========================================
  setupComparisonMode(scope) {
    // This function's UI logic is now handled by _setupAdvancedUI.
  }

  showComparisonView() {
    const { modalElement: panelElement, fragment: panelFragment } = this._createThemedModalFromTemplate('specifikit-comparison-panel-template', '.specifikit-comparison-panel');
    if (!panelElement) return;

    const templates = {
      item: document.getElementById('specifikit-comparison-item-template'),
      current: document.getElementById('specifikit-comparison-current-item-template'),
      empty: document.getElementById('specifikit-comparison-empty-slot-template')
    };

    if (Object.values(templates).some(t => !t)) {
      this.logger.error('One or more comparison templates are missing.');
      this.showNotification(this.i18n.ar.error_modal, 'error');
      return;
    }
    
    const gridContainer = panelElement.querySelector('[data-specifikit-template-grid-container]');
    
    panelElement.querySelector('[data-specifikit-template-title]').textContent = this.i18n.comparison.title;

    const currentItemFragment = templates.current.content.cloneNode(true);
    const currentItemContainer = currentItemFragment.querySelector('.specifikit-comparison-item');
    
    currentItemContainer.querySelector('[data-specifikit-template-name]').textContent = this.i18n.comparison.current_config;
    currentItemContainer.querySelector('.specifikit-btn-current-design').textContent = this.i18n.comparison.currently_viewing;
    currentItemContainer.querySelector('[data-specifikit-template-details-heading]').textContent = this.i18n.comparison.details_heading;
    
    const scope = this.customizer.ui.modal.isOpen ? this.customizer.ui.modal.instance : this.container;
    const activeImage = scope.querySelector('img:not([style*="display: none"])');
    const currentState = this.customizer.state;
    const currentDesc = this.buildDescriptionFromState(currentState);

    const currentImgElement = currentItemContainer.querySelector('[data-specifikit-template-img]');
    this._setupComparisonImage(currentImgElement, activeImage?.src || '');
    currentImgElement.alt = this.i18n.comparison.current_config;

    currentItemContainer.querySelector('[data-specifikit-template-description]').textContent = currentDesc;
    currentItemContainer.querySelector('[data-specifikit-template-price]').textContent = this.customizer.formatMoney(this.customizer.getTotalPrice() * 100);
    gridContainer.appendChild(currentItemFragment);
    
    const MAX_SAVED_CONFIGS = 3;
    const savedForCompare = this.state.savedConfigs.slice(-MAX_SAVED_CONFIGS);
    
    savedForCompare.forEach(config => {
      const itemFragment = templates.item.content.cloneNode(true);
      const itemElement = itemFragment.querySelector('.specifikit-comparison-item');
      const description = this.buildDescriptionFromState(config.state);
      
      const imgElement = itemElement.querySelector('[data-specifikit-template-img]');
      this._setupComparisonImage(imgElement, config.imageUrl);
      imgElement.alt = this.escapeHtml(config.name);

      itemElement.querySelector('[data-specifikit-template-name]').textContent = this.escapeHtml(config.name);
      itemElement.querySelector('[data-specifikit-template-description]').textContent = description;
      itemElement.querySelector('[data-specifikit-template-price]').textContent = this.customizer.formatMoney(config.price * 100);
      
      const loadBtn = itemElement.querySelector('.specifikit-btn-load-compare');
      loadBtn.dataset.id = config.id;
      loadBtn.textContent = this.i18n.comparison.load_this;
      itemElement.querySelector('[data-specifikit-template-details-heading]').textContent = this.i18n.comparison.details_heading;
      
      gridContainer.appendChild(itemFragment);
    });

    if (savedForCompare.length < MAX_SAVED_CONFIGS) {
      const emptySlotFragment = templates.empty.content.cloneNode(true);
      const emptySlotElement = emptySlotFragment.querySelector('.specifikit-comparison-empty-slot');
      emptySlotElement.querySelector('[data-specifikit-template-empty-text]').textContent = this.i18n.comparison.no_saved_configs;
      gridContainer.appendChild(emptySlotFragment);
    }
    
    document.body.appendChild(panelFragment);
    document.body.classList.add('specifikit-comparison-open');

    const close = () => {
      panelElement.remove();
      document.body.classList.remove('specifikit-comparison-open');
    };
    
    panelElement.querySelector('.specifikit-comparison-close').addEventListener('click', close);

    // Attach event listener for load buttons, accordion triggers, and new image expand/collapse
    panelElement.addEventListener('click', (e) => {
      const loadBtn = e.target.closest('.specifikit-btn-load-compare');
      if (loadBtn) {
        const configId = parseInt(loadBtn.dataset.id, 10);
        const cfg = this.state.savedConfigs.find((c) => c.id === configId);
        if (cfg) this.applyConfiguration(cfg);
        close();
        return;
      }

      const accordionTrigger = e.target.closest('.specifikit-comparison-accordion-trigger');
      if (accordionTrigger) {
          const accordion = accordionTrigger.closest('.specifikit-comparison-accordion');
          if (accordion) {
              const isOpening = !accordion.classList.contains('is-open');
              accordion.classList.toggle('is-open');
              accordionTrigger.setAttribute('aria-expanded', String(isOpening));
          }
          return;
      }

      // START: IMAGE EXPAND/COLLAPSE LOGIC
      const expandBtn = e.target.closest('[data-specifikit-compare-expand]');
      if (expandBtn) {
          const item = expandBtn.closest('.specifikit-comparison-item');
          if (item) {
              // Un-expand any other item that might be expanded
              panelElement.querySelectorAll('.specifikit-comparison-item.is-expanded').forEach(expandedItem => {
                  if (expandedItem !== item) {
                      expandedItem.classList.remove('is-expanded');
                      const otherExpandBtn = expandedItem.querySelector('[data-specifikit-compare-expand]');
                      if (otherExpandBtn) otherExpandBtn.setAttribute('aria-expanded', 'false');
                  }
              });
              // Expand the current one
              item.classList.add('is-expanded');
              expandBtn.setAttribute('aria-expanded', 'true');
          }
          return;
      }
  
      const collapseBtn = e.target.closest('[data-specifikit-compare-collapse]');
      if (collapseBtn) {
          const item = collapseBtn.closest('.specifikit-comparison-item');
          if (item) {
              item.classList.remove('is-expanded');
              const expandBtn = item.querySelector('[data-specifikit-compare-expand]');
              if (expandBtn) expandBtn.setAttribute('aria-expanded', 'false');
          }
      }
      // END: IMAGE EXPAND/COLLAPSE LOGIC
    });
  }

  _setupComparisonImage(imgElement, imageUrl) {
    if (!imgElement) return;
    imgElement.src = imageUrl || '';
    imgElement.onerror = () => {
        this.logger.warn('Comparison View: Image failed to load.', imageUrl);
        imgElement.style.display = 'none';
        const wrapper = imgElement.closest('.specifikit-comparison-image-wrapper');
        if (wrapper && !wrapper.querySelector('.specifikit-comparison-placeholder')) {
            const placeholder = document.createElement('div');
            placeholder.className = 'specifikit-comparison-placeholder';
            const text = document.createElement('p');
            const placeholderText = this.i18n?.cart?.preview_unavailable || 'Preview Unavailable';
            text.textContent = placeholderText;
            const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            iconSvg.setAttribute('viewBox', '0 0 24 24');
            iconSvg.setAttribute('fill', 'currentColor');
            iconSvg.innerHTML = `<path d="M21.71 3.29c-.39-.39-1.02-.39-1.41 0L12 11.59 3.7 3.29a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41L10.59 13 2.29 21.29a.9959.9959 0 0 0 0 1.41c.39.39 1.02.39 1.41 0L12 14.41l8.29 8.29c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 13l8.3-8.29c.38-.39.38-1.03 0-1.42z M19 5v11.59l-9.59-9.59L19 5z M5 19v-11.59l9.59 9.59L5 19z"/>`;
            placeholder.appendChild(iconSvg);
            placeholder.appendChild(text);
            wrapper.appendChild(placeholder);
        }
    };
  }

  buildDescriptionFromState(state) {
    if (!state) return '';
    const descParts = [state.size ? this.customizer.capitalize(state.size) : ''];
    Object.values(state.componentSelections || {}).forEach(sel => {
      if (Array.isArray(sel)) { sel.forEach(s => s.name && descParts.push(s.name)); }
      else if (sel && sel.name) { descParts.push(sel.name); }
    });
    return this.customizer.ui.escapeHtml(descParts.filter(Boolean).join(' | '));
  }

  // ==========================================
  // FEATURE: Wishlist
  // ==========================================
  setupWishlist(scope) {
    const placeholder = scope.querySelector('[data-specifikit-placeholder="wishlist-button"]');
    if (!placeholder) {
      this.logger.warn("Advanced: Wishlist placeholder not found in scope");
      return;
    }
    
    if (placeholder.querySelector('.specifikit-btn-add-wishlist')) {
      this.logger.log('Wishlist: Button already exists in this placeholder');
      return;
    }
    
    const btn = document.createElement('button');
    btn.className = 'specifikit-btn specifikit-btn-md specifikit-btn-secondary specifikit-btn-add-wishlist';
    btn.type = 'button';
    btn.title = this.i18n.wishlist.tooltip;
    btn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
      <span class="specifikit-btn-add-wishlist-text">${this.i18n.wishlist.button}</span>
    `;
    btn.addEventListener('click', () => this.addToWishlist());
    placeholder.appendChild(btn);
  }

  addToWishlist() {
    const scope = this.customizer.ui.modal.isOpen ? this.customizer.ui.modal.instance : this.container;
    const activeImage = scope.querySelector('img:not([style*="display: none"])');
    const state = this.customizer.state;
    const nameParts = [state.size ? this.customizer.capitalize(state.size) : ''];
    Object.values(state.componentSelections).forEach(sel => {
      if (Array.isArray(sel)) { sel.forEach(s => s.name && nameParts.push(s.name)); }
      else if (sel && sel.name) { nameParts.push(sel.name); }
    });

    let name = `${this.config.productTitle} (${nameParts.filter(Boolean).join(', ')})`;
    Object.values(state.addons).forEach(addon => { 
      if (addon.type === 'text' && addon.value) name += ` - ${this.escapeHtml(addon.value)}`; 
    });

    const config = {
      productId: state.variantId,
      name: name,
      state: JSON.parse(JSON.stringify(state)),
      price: this.customizer.getTotalPrice(),
      imageUrl: activeImage?.src || '',
      timestamp: new Date().toISOString(),
      url: `${window.location.origin}${window.location.pathname}?config=${btoa(JSON.stringify(state))}`
    };

    let wishlist = this.loadWishlistFromStorage();
    wishlist.push(config);
    localStorage.setItem('specifikit_wishlist', JSON.stringify(wishlist));
    this.showNotification(this.i18n.wishlist.success, 'success');
    this.updateWishlistCount(wishlist.length);
  }

  loadWishlistFromStorage() {
    try {
      return JSON.parse(localStorage.getItem('specifikit_wishlist') || '[]');
    } catch {
      return [];
    }
  }

  updateWishlistCount(count) {
    const wishlistCount = typeof count === 'number' ? count : this.loadWishlistFromStorage().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = wishlistCount;
      el.style.display = wishlistCount > 0 ? 'inline-block' : 'none';
    });
  }

  // ==========================================================
  // FEATURE: Admin Cart Checker
  // ==========================================================
  setupAdminCartChecker() {
    this.logger.info('👮 Admin Assist: Initializing cart snippet checker.');

    // MULTI-LAYERED TRIGGER:
    // 1. Listen for our custom event, dispatched after 'Add to Cart'. This is the most reliable trigger.
    document.addEventListener('specifikit:adminCartCheck', () => this.checkForMissingCartSnippet());

    // 2. Use a MutationObserver as a fallback for when the cart is opened manually.
    const cartDrawer = document.querySelector('cart-drawer, #cart-drawer, .cart-drawer, [id*="cart-drawer"]');
    const targetNode = cartDrawer || document.querySelector('[data-section-type*="cart"]') || document.body;

    const observer = new MutationObserver(async (mutationsList) => {
      const isCartLikelyVisible = cartDrawer ? !cartDrawer.hasAttribute('hidden') && cartDrawer.offsetWidth > 0 : false;
      if (isCartLikelyVisible || document.location.pathname.startsWith('/cart')) {
        await this.checkForMissingCartSnippet();
      }
    });

    observer.observe(targetNode, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'hidden', 'open', 'active']
    });

    // 3. Initial check on page load if already on the cart page.
    if (document.location.pathname.startsWith('/cart')) {
      this.checkForMissingCartSnippet();
    }
  }

  async checkForMissingCartSnippet() {
    if (this.isCheckingCartSnippet) return;
    this.isCheckingCartSnippet = true;
    
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error(`Cart fetch failed with status ${res.status}`);
      const cart = await res.json();
      
      const hasCustomizedItems = cart.items.some(item => item.properties && item.properties._customizations);
      
      if (!hasCustomizedItems) {
        this.logger.log('Admin Assist: No customized items in cart. Check not needed.');
        this.adminNudge.hide(); // Hide any existing nudge if cart is now empty of custom items.
        return;
      }

      // RESILIENT POLLING MECHANISM
      // This waits for theme animations by polling for the snippet instead of checking just once.
      let attempts = 0;
      const maxAttempts = 8; // 8 attempts * 250ms = 2 seconds total
      const interval = setInterval(() => {
        attempts++;
        const isSnippetPresent = document.querySelector('.specifikit-cart-customization-details');

        if (isSnippetPresent) {
          // SUCCESS: Snippet found.
          clearInterval(interval);
          this.logger.info(`Admin Assist: Cart snippet check passed after ${attempts} attempt(s).`);
          this.adminNudge.hide();
        } else if (attempts >= maxAttempts) {
          // FAILURE: Snippet not found after 2 seconds.
          clearInterval(interval);
          this.logger.warn(`Admin Assist: Customized item found, but render snippet is MISSING after ${maxAttempts} attempts.`);
          this.adminNudge.show({ snippet_name: 'specifikit-cart-display.liquid' }, 'error');
        }
      }, 250);

    } catch (e) {
      this.logger.error('Admin Assist: Error checking cart state.', e);
    } finally {
      // Use a longer timeout to prevent rapid re-checks
      setTimeout(() => { this.isCheckingCartSnippet = false; }, 1000);
    }
  }

  // ==========================================
  // Helper Methods
  // ==========================================
  loadModelViewerScript() {
    if (this.modelViewerLoaded) return Promise.resolve();
    if (window.customElements.get('model-viewer')) { 
      this.modelViewerLoaded = true; 
      return Promise.resolve(); 
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.4.0/model-viewer.min.js';
      script.async = true;
      script.onload = () => { this.modelViewerLoaded = true; resolve(); };
      script.onerror = () => { 
        this.logger.error('Failed to load <model-viewer> script.'); 
        reject(new Error('Script load failed')); 
      };
      document.body.appendChild(script);
    });
  }
  
  populateTemplateText(parentElement, textMap) {
    for (const [selector, text] of Object.entries(textMap)) {
      const el = parentElement.querySelector(selector);
      if (el) {
        el.textContent = text;
      } else {
        this.logger.warn(`Template element with selector "${selector}" not found for i18n.`);
      }
    }
  }

  showConfirmDialog(options = {}) {
    return new Promise(resolve => {
      const {
        title = 'Are you sure?',
        prompt = 'This action cannot be undone.',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        isDestructive = false
      } = options;

      const { modalElement: dialog, fragment: dialogFragment } = this._createThemedModalFromTemplate('specifikit-save-dialog-template', '.specifikit-utility-modal');
      if (!dialog) {
          resolve(false);
          return;
      }
      
      const titleEl = dialog.querySelector('[data-specifikit-template-title]');
      const promptEl = dialog.querySelector('[data-specifikit-template-prompt]');
      const inputEl = dialog.querySelector('[data-specifikit-template-input]');
      const confirmBtn = dialog.querySelector('[data-specifikit-template-confirm-btn]');
      const cancelBtn = dialog.querySelector('[data-specifikit-template-cancel-btn]');
      
      titleEl.textContent = title;
      promptEl.innerHTML = this.unescapeHtml(prompt);
      confirmBtn.textContent = confirmText;
      cancelBtn.textContent = cancelText;
      inputEl.style.display = 'none';
      
      if (isDestructive) {
        confirmBtn.style.backgroundColor = '#ef4444';
        confirmBtn.style.color = '#ffffff';
      }

      document.body.appendChild(dialogFragment);

      const close = (result) => {
        dialog.remove();
        resolve(result);
      };

      dialog.querySelector('.specifikit-dialog-overlay').addEventListener('click', () => close(false));
      cancelBtn.addEventListener('click', () => close(false));
      confirmBtn.addEventListener('click', () => close(true));
    });
  }
  
  formatDate(date) {
    if (!(date instanceof Date)) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  escapeHtml(str) {
    if (!str) return '';
    const p = document.createElement('p');
    p.textContent = str;
    return p.innerHTML;
  }

  unescapeHtml(escapedStr) {
    if (!escapedStr) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = escapedStr;
    return textarea.value;
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `specifikit-notification specifikit-notification-${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);
    requestAnimationFrame(() => notification.classList.add('specifikit-show'));
    setTimeout(() => {
      notification.classList.remove('specifikit-show');
      notification.addEventListener('transitionend', () => notification.remove());
    }, 4000);
  }
}

// ==========================================
// Initialize on Core Ready
// ==========================================
document.addEventListener('specifikitCore:loaded', (e) => {
  try {
    if (window.advancedFeatures) {
      console.warn("Advanced features already initialized. Skipping.");
      return;
    }
    window.advancedFeatures = new SpecifikitAdvancedFeatures(e.detail.instance);
  } catch (error) {
    // This top-level error happens before the logger is instantiated, so a native console.error is appropriate.
    console.error('❌ Critical error initializing advanced features:', error);
  }
});

/**
 * ============================================================================
 * END Specifikit Advanced Features JS FILE
 * ============================================================================
 */